import React, {useState, useEffect} from 'react';
import {useDispatch} from 'react-redux';
import AddDataArchiveUI from './AddDataArchiveUI';
import {fetchError} from '../../../redux/actions';
import {getAddDataArchive} from '../apis/addDataArchive';
import jwtAxios from '@crema/services/auth/jwt-auth';
import {saveAs} from 'file-saver';

const AddDataArchiveContainer = () => {
  const [dataArchive, setDataArchive] = useState([]);
  const [archiveCount, setArchiveCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [filters, setFilters] = useState({
    dateRange: [null, null],
  });

  const dispatch = useDispatch();

  useEffect(() => {
    loadDataArchive(); // Fetch data whenever page, filters, or rowsPerPage change
  }, [page, filters, rowsPerPage]);

  const loadDataArchive = async () => {
    setLoading(true);
    try {
      // First get the basic report data
      const {data, count} = await getAddDataArchive(page, filters, rowsPerPage);

      // Then get the archive counts for each report
      const archiveCountsResponse = await jwtAxios.get(
        '/archive/available-database-reports',
      );
      const archiveCounts = archiveCountsResponse.data?.data || [];

      // Merge the data with archive counts
      const processedData = (data || []).map((row) => {
        const archiveInfo = archiveCounts.find(
          (archive) => archive.id === row.report_id,
        );
        return {
          ...row,
          id: row.report_id, // Use a unique field or create one
          archive_count: archiveInfo ? archiveInfo.archive_count : 0,
          has_data: archiveInfo ? archiveInfo.has_data : false,
        };
      });

      setDataArchive(processedData);
      setArchiveCount(count || 0);
    } catch (error) {
      dispatch(fetchError(error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateReport = async (report_id) => {
    try {
      // Use the new Excel generation endpoint
      const response = await jwtAxios.get(
        `/archive/report/${report_id}/excel`,
        {
          responseType: 'blob', // Important for binary data
        },
      );

      // Create blob from response data
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });

      // Generate filename with current date
      const currentDate = new Date().toISOString().split('T')[0];
      const fileName = `archive_report_${report_id}_${currentDate}.xlsx`;

      saveAs(blob, fileName);
      console.log('Report downloaded successfully:', fileName);
    } catch (error) {
      // Check if it's a "no data" error
      if (error.response && error.response.data && error.response.data.error) {
        const errorMessage = error.response.data.error;
        if (
          errorMessage.includes('contains no archive data') ||
          errorMessage.includes('No data found for the provided report ID')
        ) {
          dispatch(
            fetchError(
              'This report contains no data to export. Please select a report that has archive data.',
            ),
          );
        } else {
          dispatch(fetchError(`Report generation failed: ${errorMessage}`));
        }
      } else {
        dispatch(fetchError(`Report generation failed: ${error.message}`));
      }
    }
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (event, newPageSize) => {
    setRowsPerPage(newPageSize);
    setPage(0); // Reset to first page when page size changes
  };

  const handleDateRangeChange = (newDateRange) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      dateRange: newDateRange,
    }));
  };

  const handleApplyFilters = () => {
    loadDataArchive(); // Re-fetch data with applied filters
  };

  const handleClearFilters = () => {
    setFilters({
      dateRange: [null, null],
    });
    setPage(0); // Reset to the first page
    loadDataArchive(); // Re-fetch data after clearing filters
  };

  return (
    <AddDataArchiveUI
      dataArchive={dataArchive}
      archiveCount={archiveCount}
      page={page}
      rowsPerPage={rowsPerPage}
      loading={loading}
      onPageChange={handlePageChange}
      onPageSizeChange={handlePageSizeChange}
      filters={filters}
      setFilters={setFilters}
      handleDateRangeChange={handleDateRangeChange}
      onApply={handleApplyFilters}
      onClear={handleClearFilters}
      handleGenerateReport={handleGenerateReport}
    />
  );
};

export default AddDataArchiveContainer;
